import { normalizeAgentAvatarSrc } from "~/lib/agent-avatar-url";
import {
  marketplaceIndustryLabel,
  marketplaceRoleLabel,
} from "~/lib/marketplace/constants";
import {
  MOONLIGHT_CATEGORIES,
  sortMoonlightAgents,
  type MoonlightAgentListItem,
} from "~/lib/marketplace/moonlight-home";
import { defaultAvatarUrlForAgentName } from "~/lib/siteglue/ai-employee-role-catalog";
import { SITEGLUE_PUBLIC_AGENTS_DISPLAY_LIMIT } from "~/lib/siteglue/ai-employees-showcase";
import type { SiteGlueMarketingChannelId } from "~/lib/siteglue/communication-channels";
import type { IndustryVertical } from "~/lib/utils/industry-profile";

const VERTICAL_INDUSTRY_LABELS: Record<IndustryVertical, string> = {
  healthcare: "Healthcare",
  legal: "Legal",
  financial: "Financial Services",
  real_estate: "Real Estate",
  education: "Education",
  home_services: "Home Services",
  retail: "Retail",
  hospitality: "Hospitality",
  fitness: "Fitness",
  automotive: "Automotive",
  manufacturing: "Manufacturing",
  consulting: "Professional Services",
  nonprofit: "Non-Profit",
  general: "Business",
};

/**
 * Prefer marketplace industry when set; if missing/"other", use SIC vertical label.
 * Never surface "Other" on marketing hero cards.
 */
export function resolveHeroIndustryLabel(
  industry: string | null | undefined,
  vertical: IndustryVertical | null | undefined,
): string {
  const marketplace = marketplaceIndustryLabel(industry).trim();
  if (marketplace && marketplace.toLowerCase() !== "other") {
    return marketplace;
  }
  if (vertical) {
    const fromCategory = MOONLIGHT_CATEGORIES.find((c) =>
      c.verticals.includes(vertical),
    )?.label;
    if (fromCategory && fromCategory !== "All") return fromCategory;
    return VERTICAL_INDUSTRY_LABELS[vertical] ?? "Business";
  }
  return "Business";
}

/** Maps staffing-agent channel flags → homepage signature channel ids. */
const CHANNEL_KEY_TO_MARKETING: Record<string, SiteGlueMarketingChannelId> = {
  call: "phone",
  aiChat: "website_chat",
  sms: "sms",
  email: "email",
  video: "video_calls",
  whatsapp: "whatsapp",
  messenger: "messenger",
};

export type HomeHeroPublishedEmployeeCard = {
  id: string;
  /** Live demo profile (`/agents/:id?try=1`). */
  profileHref: string;
  agentName: string;
  role: string;
  companyName: string;
  industryLabel: string;
  avatarSrc: string;
  activeChannelIds: SiteGlueMarketingChannelId[];
  responsibilities: string[];
  phoneDisplay?: string;
  emailAddress?: string;
  websiteDisplay?: string;
};

const PLATFORM_EMAIL_DOMAINS = new Set(["easygpt.builders", "siteglue.ai"]);

export function isPlatformMarketingEmail(email: string | null | undefined): boolean {
  if (!email?.includes("@")) return false;
  const domain = email.split("@")[1]?.trim().toLowerCase();
  return domain ? PLATFORM_EMAIL_DOMAINS.has(domain) : false;
}

export function normalizeWebsiteDisplay(url: string | null | undefined): string | undefined {
  if (!url?.trim()) return undefined;
  return url.trim().replace(/^https?:\/\//i, "").replace(/^www\./i, "");
}

/** Prefer project / agent website URLs; avoid platform no-reply email domains. */
export function resolveAgentWebsiteDisplay(input: {
  websiteUrl?: string | null;
  aiAgentWebsiteUrl?: string | null;
  emailAddress?: string | null;
}): string | undefined {
  const fromProject = normalizeWebsiteDisplay(input.websiteUrl);
  if (fromProject) return fromProject;
  const fromAgent = normalizeWebsiteDisplay(input.aiAgentWebsiteUrl);
  if (fromAgent) return fromAgent;
  if (input.emailAddress && !isPlatformMarketingEmail(input.emailAddress)) {
    return normalizeWebsiteDisplay(input.emailAddress.split("@")[1]);
  }
  return undefined;
}

export function formatAgentPhoneDisplay(phone: string | null | undefined): string | undefined {
  if (!phone?.trim()) return undefined;
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone.trim();
}

export function activeMarketingChannelsFromAgent(
  channels: Record<string, boolean> | null | undefined,
): SiteGlueMarketingChannelId[] {
  if (!channels) return ["website_chat"];
  const active = Object.entries(CHANNEL_KEY_TO_MARKETING)
    .filter(([key]) => channels[key])
    .map(([, id]) => id);
  return active.length > 0 ? active : ["website_chat"];
}

export function mapStaffingAgentToHeroCard(
  agent: MoonlightAgentListItem,
): HomeHeroPublishedEmployeeCard {
  const agentName = agent.aiAgent?.name?.trim() || agent.title;
  const role =
    agent.aiAgent?.jobTitle?.trim() ||
    marketplaceRoleLabel(agent.role) ||
    "AI Employee";
  const avatarSrc =
    normalizeAgentAvatarSrc(agent.aiAgent?.base64Avatar) ??
    normalizeAgentAvatarSrc(agent.videoThumbnailUrl) ??
    defaultAvatarUrlForAgentName(agentName);

  return {
    id: agent.id,
    profileHref: `${agent.profilePath}?try=1`,
    agentName,
    role,
    companyName: agent.title,
    industryLabel: resolveHeroIndustryLabel(agent.industry, agent.vertical),
    avatarSrc,
    activeChannelIds: activeMarketingChannelsFromAgent(agent.channels),
    responsibilities: agent.responsibilities.filter(Boolean).slice(0, 3),
    phoneDisplay: formatAgentPhoneDisplay(agent.aiAgent?.phoneNumber),
    emailAddress: agent.aiAgent?.emailAddress ?? undefined,
    websiteDisplay: resolveAgentWebsiteDisplay({
      websiteUrl: agent.websiteUrl,
      aiAgentWebsiteUrl: agent.aiAgent?.websiteUrl,
      emailAddress: agent.aiAgent?.emailAddress,
    }),
  };
}

/** Same published set as `/agents` (unfiltered, featured sort, display cap). */
export function publishedAgentsForHomeHero(
  agents: MoonlightAgentListItem[],
): HomeHeroPublishedEmployeeCard[] {
  return sortMoonlightAgents(agents, "featured")
    .slice(0, SITEGLUE_PUBLIC_AGENTS_DISPLAY_LIMIT)
    .map(mapStaffingAgentToHeroCard);
}
